import { sha256 } from '@noble/hashes/sha256'
import { artBoardActions } from '@store/reducers/artboard'
import { metaActions } from '@store/reducers/manager/meta/actions'
import { socket } from '@store/socketSaga'
import * as ActionTypes from '@storeOld/actions'
import {
  NotificationType,
  notificationActionCreators,
} from '@storeOld/reducers/notification'
import { setSyncStatus } from '@storeOld/reducers/ui'
import axios from 'axios'
import stringify from 'json-stable-stringify'

import { reportClientEvent } from '@/services/clientEvents'
import { createCommitSyncStatus } from './commitSyncStatus'

// A dropped commit repeats on every keystroke until the socket is back:
// one report per reason per interval is enough to see it.
const DROP_REPORT_INTERVAL = 5000
const lastDropReport = {}

function reportDroppedCommit(reason, projectId) {
  const now = Date.now()

  if (now - (lastDropReport[reason] || 0) < DROP_REPORT_INTERVAL) {
    return
  }

  lastDropReport[reason] = now
  reportClientEvent({ type: 'commit_dropped', reason, projectId })
}

function describeCommitError(err) {
  if (err.response) {
    return `http_${err.response.status}`
  }

  return err.code || 'network'
}

function getStateHash(state) {
  const json = stringify(state)
  const hash = sha256(new TextEncoder().encode(json))
  return Array.from(hash)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

let isSending = false
let nextBatch = []
let hasFailed = false

export function resetCommitState() {
  hasFailed = false
}

const handleBeforeUnload = (event) => {
  event.preventDefault()

  const message =
    'Are you sure you want to leave this page. Changes you made may not be saved!'

  event.returnValue = message
  return message
}

function commitProjectRequest({
  projectId,
  actions,
  stateHash,
  onSendingStart,
  onCompleted,
  onFailure,
}) {
  return new Promise((resolve, reject) => {
    if (hasFailed) {
      return reject(new Error('Batching is halted due to a previous failure.'))
    }

    if (isSending) {
      nextBatch.push(...actions)
      return Promise.resolve().then(resolve).catch(reject)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    isSending = true
    onSendingStart?.()

    const token = localStorage.getItem('token')

    axios
      .post(
        `/projects/${projectId}/commit`,
        {
          actions,
          stateHash,
        },
        {
          baseURL: import.meta.env.VITE_SYNC_HTTP_URL,
          headers: {
            'X-Auth-Socket-Id': socket.id,
            'X-Auth-Token': token,
            'X-Password-Token': localStorage.getItem(`pw_${projectId}`),
          },
        }
      )
      .then((res) => {
        resolve(res)
      })
      .catch((err) => {
        hasFailed = true
        nextBatch = []
        reportClientEvent({
          type: 'commit_failed',
          reason: describeCommitError(err),
          projectId,
        })
        onFailure?.(err)
        reject(err)
      })
      .finally(() => {
        isSending = false

        window.removeEventListener('beforeunload', handleBeforeUnload)

        if (!hasFailed && nextBatch.length > 0) {
          const batchToSend = nextBatch
          nextBatch = []
          commitProjectRequest({
            projectId,
            actions: batchToSend,
            stateHash,
            onSendingStart,
            onCompleted,
            onFailure,
          }).catch(() => {})

          return
        }

        if (!hasFailed) {
          onCompleted?.()
        }
      })
  })
}

export const commitMiddleware = (store) => {
  const { setSyncingStatus, setSuccessStatusWithAutoHide } =
    createCommitSyncStatus(store)

  return (next) => (action) => {
    if (
      action.type !== ActionTypes.COMMIT_PROJECT_REQUEST &&
      action.type !== ActionTypes.COMMIT_PROJECT_REQUEST_PURE
    ) {
      return next(action)
    }

    const state = store.getState()

    const {
      manager: {
        meta: {
          id: projectId,
          access: { canEdit, canEditContent },
        },
      },
      artboard: { socketOffline, saveFailed, activityHistory, mode },
    } = state

    // The server refused a commit and nothing after it will be taken either:
    // say so for good and offer the only way out. The socket is alive here, so
    // this is not `socketOffline` — the artboard goes off on `saveFailed`.
    const notifySaveFailed = () => {
      store.dispatch(
        notificationActionCreators.setSocketNotification({
          type: NotificationType.SAVE_FAILED,
          callback: () => {
            // Deliberate full reload: what could not be saved is gone either
            // way, and a fresh load is the state the server actually holds.
            window.location.reload()
          },
        })
      )
    }

    const handleCommitFailure = () => {
      store.dispatch(artBoardActions.setSaveFailed(true))
      store.dispatch(setSyncStatus('hidden'))

      notifySaveFailed()
    }

    // Saving is down: the edit is not applied at all. Applied locally and
    // never sent, it stayed on screen and was gone after a reload.
    if (projectId && saveFailed) {
      reportDroppedCommit('save_failed', projectId)
      notifySaveFailed()

      return
    }

    if (projectId && (socketOffline || !socket.connected || !socket.id)) {
      reportDroppedCommit('offline', projectId)
      store.dispatch(
        notificationActionCreators.setNotification({
          type: NotificationType.PROBLEM_WITH_SAVING_CHANGES,
        })
      )

      return
    }

    const actions = action.payload ? [].concat(action.payload) : []
    if (action.type === ActionTypes.COMMIT_PROJECT_REQUEST) {
      actions.forEach(next)
    }

    const stateHash = getStateHash({
      project: state.manager.project,
      relations: state.relations,
    })

    // The activity history is open and the project on screen may be a
    // snapshot of the past: the edit stays local.
    if (activityHistory || mode === 'activity_history') {
      reportDroppedCommit('history', projectId)

      return
    }

    if (!canEdit && !canEditContent) {
      return
    }

    if (!projectId) {
      store.dispatch({
        type: metaActions.updateUpdatedAt.type,
        payload: Date.now(),
      })

      return
    }

    commitProjectRequest({
      projectId,
      actions,
      stateHash,
      onSendingStart: setSyncingStatus,
      onCompleted: setSuccessStatusWithAutoHide,
      onFailure: handleCommitFailure,
    })
      .then((res) => {
        if (res) {
          const { data } = res
          // biome-ignore lint/suspicious/noConsole: intentional
          console.log(
            `Commit to project: ${projectId} ${stateHash}, ${data.stateHash}`
          )

          if (data.stateHash !== stateHash) {
            console.error(
              `Mismatched hash when commit to project: ${projectId}: ${stateHash}, response: ${data.stateHash}`
            )
          }
        }
      })
      .catch((error) => {
        console.error(`Problem with saving action: ${action.type}`, error)
      })
  }
}
